import { useEffect, useState } from 'react';

import { Button, Checkbox, FieldLabel, Modal, NumericInput, showSnackbar, TabPage, TabView } from 'fluxo-ui';
import { CheckIcon, EditIcon, ListIcon, PlusIcon, SearchIcon, TrashIcon } from 'fluxo-ui/icons';

import questionService from '../../services/question.service';
import { QuestionSearch } from '../question';

import type { ComponentEvent } from 'fluxo-ui';

interface SelectedQuestion {
    questionId: string;
    title: string;
    type: string;
    marks?: number;
    timeSeconds?: number;
    shuffleOptions?: boolean;
    order: number;
}

interface QuestionSelectorProps {
    selectedQuestions?: SelectedQuestion[];
    onChange?: (questions: SelectedQuestion[]) => void;
    disabled?: boolean;
}

const QuestionSelector: React.FC<QuestionSelectorProps> = ({ selectedQuestions = [], onChange, disabled = false }) => {
    const [questions, setQuestions] = useState<SelectedQuestion[]>(selectedQuestions);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<SelectedQuestion | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [availableQuestions, setAvailableQuestions] = useState<any[]>([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [searchFilters, setSearchFilters] = useState<any>({});

    useEffect(() => {
        if (showAddModal) {
            loadAvailableQuestions();
        }
    }, [showAddModal, searchFilters]);

    const loadAvailableQuestions = async () => {
        setLoadingQuestions(true);
        try {
            const result = await questionService.searchQuestions({
                ...searchFilters,
                status: 'approved',
                archivedStatus: 'active',
                limit: 50,
            } as any);
            setAvailableQuestions(result.questions || []);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load questions', 'Error', { type: 'error' });
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleUpdate = (updatedQuestions: SelectedQuestion[]) => {
        setQuestions(updatedQuestions);
        onChange?.(updatedQuestions);
    };

    const addQuestion = (question: any) => {
        const newQuestion: SelectedQuestion = {
            questionId: question._id || question.questionId,
            title: question.title,
            type: question.type,
            marks: question.defaultMarks,
            timeSeconds: question.defaultTimeSeconds,
            shuffleOptions: question.type === 'mcq_single' || question.type === 'mcq_multiple',
            order: questions.length + 1,
        };
        handleUpdate([...questions, newQuestion]);
        showSnackbar('Question added to exam', 'Success', { type: 'success' });
    };

    const removeQuestion = (questionId: string) => {
        const updated = questions
            .filter((q) => q.questionId !== questionId)
            .map((q, idx) => ({
                ...q,
                order: idx + 1,
            }));
        handleUpdate(updated);
        showSnackbar('Question removed', 'Success', { type: 'success' });
    };

    const updateQuestion = (questionId: string, updates: Partial<SelectedQuestion>) => {
        handleUpdate(questions.map((q) => (q.questionId === questionId ? { ...q, ...updates } : q)));
    };

    const moveQuestion = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= questions.length) return;

        const updated = [...questions];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        updated.forEach((q, idx) => (q.order = idx + 1));
        handleUpdate(updated);
    };

    const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0);
    const totalTime = questions.reduce((sum, q) => sum + (q.timeSeconds || 0), 0);

    return (
        <>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Questions</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            {questions.length} question{questions.length !== 1 ? 's' : ''} • {totalMarks} total marks •{' '}
                            {Math.floor(totalTime / 60)} min
                        </p>
                    </div>
                    <Button
                        label="Add Questions"
                        leftIcon={PlusIcon}
                        variant="primary"
                        size="sm"
                        onClick={() => setShowAddModal(true)}
                        disabled={disabled}
                    />
                </div>

                {/* Selected Questions List */}
                {questions.length > 0 ? (
                    <div className="space-y-3">
                        {questions.map((question, index) => (
                            <div
                                key={question.questionId}
                                className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start gap-4">
                                    {/* Order */}
                                    <div className="flex flex-col items-center space-y-1">
                                        <span className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 text-sm font-bold flex items-center justify-center">
                                            {question.order}
                                        </span>
                                        <div className="flex flex-col">
                                            <button
                                                onClick={() => moveQuestion(index, 'up')}
                                                disabled={index === 0 || disabled}
                                                className="p-1 text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Move up"
                                            >
                                                ▲
                                            </button>
                                            <button
                                                onClick={() => moveQuestion(index, 'down')}
                                                disabled={index === questions.length - 1 || disabled}
                                                className="p-1 text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Move down"
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </div>

                                    {/* Question Info */}
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <h4 className="font-medium text-slate-900 dark:text-white">{question.title}</h4>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 space-x-2">
                                                    <span className="px-2.5 py-0.5 font-medium rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                                                        {(question.type || '').replace(/_/g, ' ').toUpperCase()}
                                                    </span>
                                                    <span>ID: {question.questionId}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 ml-4">
                                                <button
                                                    onClick={() => setEditingQuestion(question)}
                                                    disabled={disabled}
                                                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                                                    title="Edit overrides"
                                                >
                                                    <EditIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => removeQuestion(question.questionId)}
                                                    disabled={disabled}
                                                    className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                                                    title="Remove"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quick Info */}
                                        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                                            <span>Marks: {question.marks}</span>
                                            <span>•</span>
                                            <span>
                                                Time: {Math.floor((question.timeSeconds || 0) / 60)}:
                                                {((question.timeSeconds || 0) % 60).toString().padStart(2, '0')}
                                            </span>
                                            {question.shuffleOptions && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-teal-600 dark:text-teal-400">Shuffle options</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700">
                        <ListIcon className="w-12 h-12 text-slate-400 opacity-40 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">No Questions Added</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                            Add questions from your question bank to create the exam
                        </p>
                        <Button label="Add First Question" leftIcon={PlusIcon} variant="primary" onClick={() => setShowAddModal(true)} />
                    </div>
                )}
            </div>

            {/* Add Questions Modal */}
            <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Questions" size="xl">
                <div className="space-y-4">
                    <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                        <TabPage header="Search & Select" leftIcon={SearchIcon}>
                            <div className="space-y-4 p-4">
                                <QuestionSearch onSearch={(filters) => setSearchFilters(filters)} />
                                <div className="space-y-2">
                                    {loadingQuestions ? (
                                        Array.from({ length: 3 }).map((_, idx) => (
                                            <div key={idx} className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded h-[60px] w-full" />
                                        ))
                                    ) : availableQuestions.length > 0 ? (
                                        availableQuestions.map((q) => (
                                            <div
                                                key={q._id}
                                                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-slate-900 dark:text-white">{q.title}</div>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                        {(q.type || '').replace(/_/g, ' ').toUpperCase()} • {q.defaultMarks} marks •{' '}
                                                        {q.defaultTimeSeconds}s
                                                    </div>
                                                </div>
                                                <Button
                                                    label={questions.some((sq) => sq.questionId === q._id) ? 'Added' : 'Add'}
                                                    leftIcon={questions.some((sq) => sq.questionId === q._id) ? CheckIcon : PlusIcon}
                                                    variant={questions.some((sq) => sq.questionId === q._id) ? 'default' : 'primary'}
                                                    size="sm"
                                                    onClick={() => addQuestion(q)}
                                                    disabled={questions.some((sq) => sq.questionId === q._id)}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                                            {searchFilters && Object.keys(searchFilters).length > 0
                                                ? 'No questions found matching your filters'
                                                : 'No approved questions available'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </TabPage>
                        <TabPage header="AI Auto-Select" leftIcon={ListIcon}>
                            <div className="p-4 text-center text-slate-500 dark:text-slate-400">AI auto-selection feature coming soon</div>
                        </TabPage>
                    </TabView>
                </div>
            </Modal>

            {/* Edit Question Modal */}
            <Modal isOpen={!!editingQuestion} onClose={() => setEditingQuestion(null)} title="Edit Question Settings" size="md">
                {editingQuestion && (
                    <div className="space-y-4">
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
                            <div className="font-medium text-slate-900 dark:text-white">{editingQuestion.title}</div>
                        </div>

                        <FieldLabel label="Marks">
                            <NumericInput
                                value={editingQuestion.marks}
                                onChange={(e: ComponentEvent<number | undefined>) => setEditingQuestion({ ...editingQuestion, marks: e.value })}
                                min={0}
                                max={100}
                            />
                        </FieldLabel>

                        <FieldLabel label="Time Limit (seconds)">
                            <NumericInput
                                value={editingQuestion.timeSeconds}
                                onChange={(e: ComponentEvent<number | undefined>) => setEditingQuestion({ ...editingQuestion, timeSeconds: e.value })}
                                min={0}
                                max={3600}
                            />
                        </FieldLabel>

                        {(editingQuestion.type === 'mcq_single' || editingQuestion.type === 'mcq_multiple') && (
                            <Checkbox
                                checked={editingQuestion.shuffleOptions}
                                onChange={(e: ComponentEvent<boolean>) =>
                                    setEditingQuestion({ ...editingQuestion, shuffleOptions: e.value })
                                }
                                label="Shuffle answer options for each student"
                            />
                        )}

                        <div className="flex justify-end gap-3">
                            <Button label="Cancel" variant="default" layout="outlined" onClick={() => setEditingQuestion(null)} />
                            <Button
                                label="Save Changes"
                                variant="primary"
                                onClick={() => {
                                    updateQuestion(editingQuestion.questionId, editingQuestion);
                                    setEditingQuestion(null);
                                }}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default QuestionSelector;
